import React, { useEffect, useState } from "react";
import GlobalWrapper from "../../../components/Wrapper/GlobalWrapper";
import CardViewSection from "./CardViewSection";
import { Form, FormikProvider, useFormik } from "formik";
import { Autocomplete, Box, Button, Divider, Grid, TextField, Typography } from "@mui/material";
import DatePickerCustom from "../../../components/DatePicker/DatePickerCustom";
import dayjs from "dayjs";
import ChartSections from "./ChartSections";
import TableSection from "./TableSection";
import { motion, AnimatePresence } from "framer-motion";
import { Icon } from "@iconify/react/dist/iconify.js";
import {
  getAllBatchNameNoPagination,
  getAllProjectsCoursesAndBatches,
  getBatchAttendance,
  getBatchDashboardChart,
  getBatchDashboardCounts,
  getBatchRefundLists,
  getRefLeadLists,
  getTutorPayments,
} from "../../../redux/features/batchSlice";
import { useDispatch } from "react-redux";
import { useSelector } from "react-redux";


const BatchDashBoardLanding = () => {
  const {
    loading: batchloading,
    dashBoardCounts,
    dashBoardChart,
    tutorPayments,
    batchAttendanceTable,
    tableLoading,
    chartLoading,
    refundTableDetails,
    refTableDetails,
    projectCourseBatches,
  } = useSelector((state) => state.batch);
  const loading = batchloading  || tableLoading || chartLoading;
  const dispatch = useDispatch();
  const [showFilters, setShowFilters] = useState(true);
  const [courseOptions, setCourseOptions] = useState([]);
  const [batchOptions, setBatchOptions] = useState([]);

  const initialValues = {
    projectId: "",
    courseId: "",
    batchId: "",
    fromDate: dayjs().startOf("month").format("YYYY-MM-DD"),
    toDate: dayjs().endOf("month").format("YYYY-MM-DD"),
  };

  const formik = useFormik({
    enableReinitialize: true,
    initialValues,
    onSubmit: (values) => {
      dispatch(getBatchDashboardCounts(dispatchParams));
      dispatch(getTutorPayments(dispatchParams));
      dispatch(getBatchAttendance(dispatchParams));
      dispatch(getBatchDashboardChart(dispatchParams));
      dispatch(getBatchRefundLists(dispatchParams));
      dispatch(getRefLeadLists(dispatchParams));
    },
  });

  const dispatchParams = {
    projectId: formik.values.projectId || null,
    courseId: formik.values.courseId || null,
    batchId: formik.values.batchId || null,
    fromDate: formik.values.fromDate,
    toDate: formik.values.toDate,
  };
  useEffect(() => {
    dispatch(getAllProjectsCoursesAndBatches());
  }, []);

  useEffect(() => {
    dispatch(getBatchDashboardCounts(dispatchParams));
    dispatch(getTutorPayments(dispatchParams));
    dispatch(getBatchAttendance(dispatchParams));
    dispatch(getBatchDashboardChart(dispatchParams));
    dispatch(getBatchRefundLists(dispatchParams));
    dispatch(getRefLeadLists(dispatchParams));
  }, []);

  const handleChange = (name, value) => {
    if (name === "projectId") {
      formik.setFieldValue("projectId", value);
      formik.setFieldValue("courseId", "");
      formik.setFieldValue("batchId", "");
    }
    else if (name === "courseId") {
      formik.setFieldValue("courseId", value);
      formik.setFieldValue("batchId", "");
    } else if (name === "batchId") {
      formik.setFieldValue("batchId", value);
    }
  };

  useEffect(()=>{
    setCourseOptions(
      projectCourseBatches.find((data) => data.project_id == formik.values.projectId)?.courses || []
    );
    setBatchOptions(
      courseOptions.find((data) => data.course_available_id == formik.values.courseId)?.batches || []
    )
  },[formik.values.projectId,formik.values.courseId,])

  return (
    <GlobalWrapper title={"Batch Dashboard"}>
      <Box mb={1} display="flex" justifyContent="flex-start">
        <Button
          endIcon={
            <Icon
              icon={showFilters ? "icon-park-solid:up-c" : "icon-park-solid:down-c"}
              width="20"
              height="20"
              style={{ color: "rgb(47 114 179)" }}
            />
          }
          size="small"
          variant="outlined"
          color="primary"
          onClick={() => setShowFilters((prev) => !prev)}
          sx={{
            minWidth: 150,
            borderRadius: 0.5,
            boxShadow: "none",
            "&:hover": {
              boxShadow: "none",
            },
          }}
        >
          {showFilters ? "Hide Filters" : "Show Filters"}
        </Button>
      </Box>
      <AnimatePresence initial={false}>
        {showFilters && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.4, ease: "easeInOut" }}
            style={{ overflow: "hidden" }}
          >
            {/* FILTER SECTION */}
            <FormikProvider value={formik}>
              <Form onSubmit={formik.handleSubmit}>
                <Box
                  mb={1}
                  mt={0.5}
                  sx={{
                    borderRadius: "8px",
                    p: 1,
                  }}
                >
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={6} md={3}>
                      <Autocomplete
                        fullWidth
                        name="projectId"
                        options={projectCourseBatches}
                        getOptionLabel={(option) => option.project_name || ""}
                        value={projectCourseBatches?.find((data) => data?.project_id == formik.values.projectId) || null}
                        onChange={(e, option) => handleChange("projectId", option?.project_id)}
                        renderInput={(params) => <TextField fullWidth autoComplete="off" {...params} label="Projects" />}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                      <Box>
                        <Autocomplete
                          name={"courseId"}
                          fullWidth
                          options={courseOptions}
                          getOptionLabel={(option) => option.course_name || ""}
                          value={
                            courseOptions.find((course) => course.course_available_id == formik.values.courseId) || null
                          }
                          onChange={(e, option) => handleChange("courseId", option?.course_available_id)}
                          renderInput={(params) => <TextField fullWidth autoComplete="off" {...params} label="Courses" />}
                        />
                      </Box>
                    </Grid>

                    <Grid item xs={12} sm={6} md={3}>
                      <Box>
                        <Autocomplete
                          name={"batchId"}
                          fullWidth
                          options={batchOptions}
                          getOptionLabel={(option) => option.course_batch_name || ""}
                          value={batchOptions?.find((data) => data?.course_batch_id == formik.values.batchId) || null}
                          onChange={(e, option) => handleChange("batchId", option?.course_batch_id)}
                          renderInput={(params) => <TextField fullWidth autoComplete="off" {...params} label="Batch" />}
                        />
                      </Box>
                    </Grid>
                    <Grid item xs={12} sm={6} md={2.2}>
                      <DatePickerCustom
                        value={dayjs(formik.values.fromDate)}
                        setValue={(date) => formik.setFieldValue("fromDate", date.format("YYYY-MM-DD"))}
                        viewDetails={["year", "month", "day"]}
                        fullWidth
                        disabled={false}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6} md={2.2}>
                      <DatePickerCustom
                        value={dayjs(formik.values.toDate)}
                        setValue={(date) => formik.setFieldValue("toDate", date.format("YYYY-MM-DD"))}
                        viewDetails={["year", "month", "day"]}
                        fullWidth
                        disabled={false}
                      />
                    </Grid>
                    <Grid
                      item
                      xs={12}
                      sm={12}
                      md={1.6}
                      sx={{
                        display: "flex",
                        alignItems: "center",
                      }}
                    >
                      <Button
                        endIcon={<Icon icon="ion:enter-sharp" width="20" height="20" style={{ color: "white" }} />}
                        size="small"
                        type="submit"
                        variant="contained"
                        color="primary"
                        sx={{
                          minWidth: 120,
                          borderRadius: 0.5,
                          boxShadow: "none",
                          "&:hover": {
                            boxShadow: "none",
                          },
                        }}
                      >
                        Submit
                      </Button>
                    </Grid>
                  </Grid>
                </Box>
              </Form>
            </FormikProvider>
          </motion.div>
        )}
      </AnimatePresence>

      {/* <Divider sx={{ mt: 1, mb: 2 }} /> */}
      {/* CARD VIEW SECTION */}
      <CardViewSection loading={loading} cardData={dashBoardCounts} />
      {dashBoardCounts.length > 0 && <Divider sx={{ mt: 3, mb: 2 }} />}

      {/* CHART SECTIONS */}
      <ChartSections dashBoardChart={dashBoardChart} year={dispatchParams?.toDate?.split("-")[0]} loading={loading} />
      <Divider sx={{ mt: 3, mb: 2 }} />
      {/* TABLE SECTIONS */}
      <TableSection
        batchAttendanceAndStudents={batchAttendanceTable}
        recentTutorPayments={tutorPayments}
        employeeReferralLeads={refTableDetails}
        refunds={refundTableDetails}
      />
    </GlobalWrapper>
  );
};

export default BatchDashBoardLanding;
